import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { SystemService } from '../services/system.service';
import { CommonService } from './common.service';
import { ModulesDataService } from './data/modules-data.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  constructor(
    private http: HttpClient, 
    private router: Router, 
    private system: SystemService, 
    private commonService: CommonService,
    private modulesDataService: ModulesDataService) { }

  login(email: string, password: string): Observable<any> {
    return this.http.post<any>(`${this.system.apiURL}/login`, { email, password }).pipe(
      tap(res => {
        localStorage.setItem('access_token', res.token);
        this.saveUser(res.user); // Save user information
        this.saveAssignedModules(res.assigned_modules);
      })
    );
  }

  register(name: string, email: string, password: string, password_confirmation: string): Observable<any> {
    return this.http.post<any>(`${this.system.apiURL}/register`, { name, email, password, password_confirmation });
  }

  logout() {
    return this.http.post<any>(`${this.system.apiURL}/logout`, {}).pipe(
      tap(() => {
        localStorage.removeItem('access_token');
        this.clearUser(); // Clear user information
        this.clearModules();
      })
    );
  }

  public get loggedIn(): boolean {
    return localStorage.getItem('access_token') !== null;
  }

  isLoggedIn(): boolean {
    // Check if there is a token stored in session storage
    const token = localStorage.getItem('access_token');
    return !!token; // Returns true if token exists, false otherwise
  }

  hasAnyRole(): boolean {
    // Implement logic to check if the user has any roles
    // For example:
    // const userRoles = this.getCurrentUserRoles();
    // return userRoles.length > 0;
    return true; // Placeholder logic, replace with actual implementation
  }

  getToken(): string | null {
    return localStorage.getItem('access_token');
  }

  saveUser(user: any): void {
    localStorage.setItem('user', JSON.stringify(user));
  }

  saveAssignedModules(modules: any) {
    localStorage.setItem('assigned_modules', JSON.stringify(modules));
  }

  getAssignedModules(): any {
    const assignedModulesString: any = localStorage.getItem('assigned_modules');
    return JSON.parse(assignedModulesString);
  }

  getUser(): any {
    const userString: any = localStorage.getItem('user');
    return JSON.parse(userString);
  }

  clearUser(): void {
    localStorage.removeItem('user');
  }

  isSuperAdmin(): boolean {
    const role = this.getUser().roles[0]?.name;
    return role === 'Super Admin';
  }

  isGroupOwner(): boolean {
    const role = this.getUser().roles[0]?.name;
    return role === 'Group Owner';
  }

  hasPermission(moduleId: number): boolean {
    const user = this.getUser();
    
    if ( user.roles[0].name == 'Group Owner' ) {
      return user.group?.group_modules?.includes(moduleId);
    }

    const assignedModules = this.getAssignedModules();

    return assignedModules.some(module => module.id === moduleId);
  }

  hasPermissionTo(moduleId: number, permissionType: 'create' | 'read' | 'update' | 'delete'): boolean {
    const assignedModules = this.getAssignedModules();

    // Find the module by ID
    const module = assignedModules.find(module => module.id === moduleId);

    if (!module) {
      return false; // No access to this module
    }

    // Check if the permission type exists and is true
    return module.pivot?.[permissionType];
  }

  isExternalCompany() {
    const role = this.getUser().roles[0]?.name;
    return role === 'External Company User';
  }

  getModules() {
    return this.commonService.getModules();
  }

  getAppModules() {
    return this.commonService.getAppModules();
  }

  clearModules() {
    this.modulesDataService.clearModules();
  }

  isEmployee(): boolean {
    return this.getUser().employee !== null;
  }

  isEmployeeOwnProfile(employeeId: number): boolean {
    return this.getUser().employee?.id === employeeId;
  }

  get employee() {
    return this.getUser()?.employee;
  }

  loginWithSSOToken(token: string): Observable<any> {
    return this.http.post<any>(`${this.system.apiURL}/sso/auth`, { token }).pipe(
      tap(res => {
        localStorage.setItem('access_token', res.token);
        this.saveUser(res.user); // Save user information
        this.saveAssignedModules(res.assigned_modules);
      })
    );;
  }
}